<template>
  <div v-cloak>
    <h1 class="sr-only">Log in to Employee Navigator</h1>
    <div >
      <div >
        <form>
          <div v-show="showCredentialsForm">
            <div >
              <div class="form-group" :class="{'has-error': missingUsername}">
                <label class="control-label" for="Username"
                  >Username</label
                >
                <div class="controls">
                  <input
                    class="form-control"
                    id="Username"
                    v-model.trim="username"
                    v-bind:disabled="processingLogin || !showCredentialsForm"
                    v-bind:aria-invalid="missingUsername ? 'true' : null"
                    aria-describedby="username-error"
                    autofocus
                  />
                  <div class="error-message" id="username-error" aria-live="polite" v-show="missingUsername"
                    >Username is required.</div
                  >
                </div>
              </div>
            </div>
            <div >
              <div class="form-group" :class="{'has-error': missingPassword}">
                <label class="control-label" for="Password"
                  >Password</label
                >
                <div class="controls">
                  <input
                    class="form-control"
                    id="Password"
                    type="password"
                    v-model.trim="password"
                    v-bind:disabled="processingLogin || !showCredentialsForm"
                    v-bind:aria-invalid="missingPassword ? 'true' : null"
                    aria-describedby="password-error"
                    autocomplete="off"
                  />
                  <div class="error-message" id="password-error" aria-live="polite" v-show="missingPassword"
                    >Password is required.</div
                  >
                </div>
              </div>
            </div>
          </div>

          <div v-show="showMfaCodeForm">
            <div >
              <div class="alert alert-info">
                <span v-if="requiresTotpCode">
                  Please open your Authentication application that you
                  registered with your Employee Navigator account and enter the
                  code in the textbox below and click the Login button.
                </span>

                <span v-if="requiresEmailCode">
                  An email containing a login code has been sent to your
                  registered email address. Please enter the code in the textbox
                  below and click the Login button.
                </span>
              </div>
            </div>

            <div >
              <div class="form-group" :class="{'has-error': missingMfaCode}">
                <label class="control-label" for="MfaCode"
                  >Code</label
                >
                <div class="controls">
                  <input
                    class="form-control"
                    id="MfaCode"
                    v-model.trim="mfaCode"
                    ref="mfaCodeInput"
                    v-bind:aria-invalid="missingMfaCode ? 'true' : null"
                    aria-describedby="mfacode-error"
                    autocomplete="off"
                  />
                  <div class="error-message" id="mfacode-error" aria-live="polite" v-show="missingMfaCode"
                    >Entering a code is required.</div
                  >
                </div>
              </div>
            </div>
          </div>

          <div >
            <div class="form-group">
              <div class="controls">
                <input
                  type="submit"
                  v-on:click.prevent="handleLogin"
                  v-bind:disabled="processingLogin"
                  class="btn btn-success login-button"
                  value="Login"
                />
              </div>
              <div v-show="errorMessage" class="error-message" role="alert">
                {{ errorMessage }}
              </div>
              <div v-show="processingLogin">
                <div style="margin-top:10px">
                  <img src="../assets/login.gif" alt="Logging in ..." />
                </div>
              </div>
            </div>
          </div>

          <div v-show="showCredentialsForm">
            <div >
              <a href="#" v-on:click.prevent="handleForgotUsername"
                >Forgot Username?</a
              >&nbsp;&nbsp;
              <a href="#" v-on:click.prevent="handleForgotPassword"
                >Forgot Password?</a
              >
            </div>
            <br />
            <div v-if="!isNativeClient">
              <a v-bind:href="benefitsAppBaseUrl + '/Account/Register'"
                >Register as a new user</a
              >
            </div>
          </div>

          <div v-show="requiresTotpCode">
            <div >
              <a href="#" v-on:click.prevent="showMissingDeviceModal"
                >Don't have your device?</a
              >
            </div>
          </div>
        </form>
      </div>
    </div>

    <info-modal
      v-bind:show-modal="showInfoModal"
      v-on:close="closeInfoModal"
      v-bind:show-footer="false"
      title="Don't Have Your Device?"
    >

      <template v-slot:body>
        If you have lost your device, contact your agency's administrator to
        <i>temporarily disable</i> your 2FA authentication. If you have a new
        device, ask the administrator to <i>deactivate</i> your existing 2FA
        registration so that you can register your new device during your next
        login.
      </template>
    </info-modal>
  </div>
</template>

<script>
import { logError, ajax } from './utils.js';
import EnModal from '@front-end/en-components/src/lib-components/EnModal.vue';

export default {
  props: ["loginHint", "loginUrl", "returnUrl", "benefitsAppBaseUrl", "isNativeClient"],
  components: {
    "info-modal": EnModal,
  },
  data() {
    return {
      username: this.loginHint,
      password: "",
      mfaCode: "",
      mfaCodeType: "",
      errorMessage: "",
      processingLogin: false,
      showCredentialsForm: true,
      showMfaCodeForm: false,
      loginButtonHasBeenClicked: false,
      showInfoModal: false,
    };
  },
  computed: {
    missingUsername() {
      return this.loginButtonHasBeenClicked && !this.username;
    },
    missingPassword() {
      return this.loginButtonHasBeenClicked && !this.password;
    },
    missingMfaCode() {
      return this.loginButtonHasBeenClicked && !this.mfaCode;
    },
    requiresTotpCode() {
      return this.showMfaCodeForm && this.mfaCodeType == "totp";
    },
    requiresEmailCode() {
      return this.showMfaCodeForm && this.mfaCodeType == "email";
    },
  },
  methods: {
    handleForgotUsername() {
      this.$emit("forgot-username-requested");
    },
    handleForgotPassword() {
      this.$emit("forgot-password-requested", {
        suggestedUsername: this.username,
      });
    },
    handleLogin() {
      this.loginButtonHasBeenClicked = true;

      if (
        this.missingUsername ||
        this.missingPassword ||
        (this.showMfaCodeForm && this.missingMfaCode)
      ) {
        return;
      }

      this.errorMessage = "";
      this.processingLogin = true;

      ajax(this.loginUrl, {
          username: this.username,
          password: this.password,
          mfaCode: this.mfaCode || null,
          returnUrl: this.returnUrl,
        })
        .then((data) => {
          if (data.errorMessage) {
            this.processingLogin = false;
            this.errorMessage = data.errorMessage;
          } else if (data.requiresMfaCode) {
            this.processingLogin = false;
            this.loginButtonHasBeenClicked = false;

            this.showCredentialsForm = false;
            this.mfaCodeType = data.mfaCodeType;
            this.showMfaCodeForm = true;
            this.$nextTick(() => this.$refs.mfaCodeInput.focus());
          } else if (data.requiresTotpSetup) {
            this.$emit("totp-setup-required", {
              username: this.username,
              password: this.password,
              qrCodeImage: data.totpSetupInfo.qrCodeImage,
              secretKey: data.totpSetupInfo.secretKey,
            });
          } else if (data.redirectUrl) {
            window.location = data.redirectUrl;
          }
        })
        .catch((err) => {
          this.processingLogin = false;
          this.errorMessage =
            "An Unknown error has occurred. Please try again later";
          logError(err);
        });
    },
    showMissingDeviceModal() {
      this.showInfoModal = true;
    },
    closeInfoModal() {
      this.showInfoModal = false;
    },
  },
};
</script>

<style scoped lang="scss">
.login-button {
  width: 100%;
  margin-top: 20px;
  margin-bottom: 10px;
}
</style>
